import Link from 'next/link';
import { ROLES } from 'src/app/reppl/permission';
import { getProfileFullNameOrUsername } from 'src/app/profile/utils';
import type { RepplResponse } from 'src/app/reppl/service';
import { useTranslation } from 'react-i18next';
import is from 'is_js';
import Avatars from 'src/components/common/avatar/avatars';
import { isDesktop } from 'react-device-detect';
import { Avatar } from 'src/components/avatar';

interface AboutRepplProps extends RepplResponse {
  isOwner: boolean;
  isLoaded: boolean;
}
export function AboutReppl({
  isOwner,
  reppl,
  creator,
  isLoaded,
}: AboutRepplProps) {
  const { t } = useTranslation();

  if (!isLoaded) {
    return (
      <div className="flex animate-pulse">
        <div className="flex-1 pr-6">
          <div className="mb-2 h-6 skeleton-line" />
          <div className="mb-2 h-6 skeleton-line" />
          <div className="mb-2 h-6 skeleton-line" />
          <div className="mb-2 h-6 skeleton-line" />
        </div>
        <div className="flex-1">
          <div className="mb-2 h-44 skeleton-line" />
        </div>
      </div>
    );
  }
  let top7 = [];
  let moreMember = [];

  if (reppl && is.not.empty(reppl.team)) {
    reppl.team.sort((a, b) => Number(a.role) - Number(b.role));

    top7 = reppl.team.slice(0, 6);
    const otherMembers = reppl.team.slice(7, 10);

    if (is.not.empty(otherMembers)) {
      moreMember = otherMembers.map((om) => {
        return om.profileImgURL;
      });
    }
  }
  const profileImgHeight = top7.length < 4 ? 18 : 14;

  return (
    <div className="px-4 md:flex md:px-auto md:my-auto reppl-container-width">
      <div className="flex-1 text-gray-900 pr-12">
        <header className="font-bold mb-6">{t('About the REPPL')}</header>
        <p>{reppl?.summary}</p>
      </div>
      <div className="flex-1 mt-10 md:mt-auto">
        <header className="mb-6">
          <span className="font-bold">{t('Meet the team')}</span>
          <span className="float-right">
            {isOwner && isDesktop && (
              <Link href={`/reppl/${reppl?.id}/team`} passHref>
                <a>{t('Manage')}</a>
              </Link>
            )}
            {!isOwner && (
              <Link href={`/reppl/${reppl?.id}/team`} passHref>
                <a>{t('View all')}</a>
              </Link>
            )}
          </span>
        </header>
        <div className="flex flex-wrap">
          {top7.map((member) => {
            return (
              <div
                className="relative w-full flex items-end md:items-baseline md:block md:w-1/4 pr-7 mb-4"
                key={member.userID}
              >
                <div
                  className={`relative rounded-full md:w-${profileImgHeight} md:h-${profileImgHeight}`}
                >
                  <Link
                    href={{
                      pathname: `/reppl/${reppl?.id}/team/${member?.username}`,
                    }}
                    passHref
                  >
                    <a className="block relative">
                      <Avatar
                        src={member['profileImgURL']}
                        name={member.username}
                        author={member}
                        width={50}
                        height={50}
                      />
                    </a>
                  </Link>
                </div>
                <div className="overflow-auto block ml-3 md:ml-auto">
                  <div className="font-bold mt-2 mb-1 truncate">
                    {getProfileFullNameOrUsername(member)}
                  </div>
                  <div className="text-gray-500 text-sm">
                    {ROLES[Number(member['role'])]}
                  </div>
                </div>
              </div>
            );
          })}
          {top7.length === 1 && (
            <div className="text-gray-500 flex-1">{creator.about}</div>
          )}
          {is.not.empty(moreMember) && (
            <div className="w-full md:w-1/4 pr-7 mb-4 flex items-center">
              <Avatars
                width="3.125rem"
                height="3.125rem"
                imgs={moreMember}
                className="absolute inset-0 m-auto md:relative"
              />
              {reppl.team.length > 10 && (
                <span className="text-gray-500 text-xs ml-2">
                  +{reppl.team.length - 10}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
